<template>
  <div class="fplc">
    <div class="fplc-header">
      <div class="fplc-avatar" :style="avatarStyle">{{ initials }}</div>
      <div class="fplc-meta">
        <div class="fplc-author">{{ item.authorName }}</div>
        <div class="fplc-sub">
          {{ $t('feed.pollLabel') }} · {{ item.poll?.totalVotes || 0 }} {{ $t('feed.votes') }}
          <span v-if="item.groupName"> · {{ item.groupName }}</span>
        </div>
      </div>
      <span v-if="item.pinned" class="fplc-tag fplc-tag--gold fplc-pinned">
        <f7-icon material="pin" :size="12" /> {{ $t('feed.important') }}
      </span>
      <FeedCardMenu
        :can-manage="canManage"
        :pinned="!!item.pinned"
        @pin="$emit('pin', { item, pinned: $event })"
        @edit="$emit('edit', item)"
        @delete="$emit('delete', item)"
      />
    </div>

    <div v-if="item.text" class="fplc-question">{{ item.text }}</div>

    <PollDisplay
      v-if="hasQuestions"
      :poll="item.poll"
      embedded
      class="fplc-display"
    />

    <template v-else>
      <div class="fplc-options">
        <div
          v-for="opt in item.poll?.options || []"
          :key="opt.id"
          class="fplc-option"
          :class="{
            'fplc-option--voted': votedId === opt.id,
            'fplc-option--disabled': hasVoted,
          }"
          role="button"
          tabindex="0"
          @click="onVote(opt)"
          @keydown.enter="onVote(opt)"
          @keydown.space.prevent="onVote(opt)"
        >
          <div class="fplc-option-bg" :style="{ width: optionPct(opt) + '%', background: optionColor(opt) }" />
          <div class="fplc-option-content">
            <span class="fplc-option-label">{{ opt.label }}</span>
            <span class="fplc-option-pct" :style="{ color: optionTextColor(opt) }">{{ optionPct(opt) }}%</span>
          </div>
        </div>
      </div>

      <div class="fplc-footer">
        <span v-if="!hasVoted" class="fplc-hint">{{ $t('feed.tapToVote') }}</span>
        <span v-else class="fplc-hint">{{ $t('feed.youVoted') }}</span>
      </div>
    </template>

    <FeedReactionBar
      :reacted="userReacted"
      :reactionType="userReactionType"
      :reactionCount="totalReactions"
      :reactions="item.reactions || []"
      :commentsCount="item.commentsCount || 0"
      :repostsCount="0"
      :share-content="shareContent"
      @react="onReact"
      @comment="$emit('comment', item)"
      @repost="showRepostComposer = true"
      @shareToChat="showShareToChat = true"
    />

    <ShareToChatSheet
      v-model:opened="showShareToChat"
      :groups="chatGroups"
      @selectGroup="onShareToChat"
    />

    <FeedRepostComposer
      v-model:opened="showRepostComposer"
      :item="item"
      :user-name="userName"
      :user-avatar="userAvatar"
      @submit="onRepostSubmit"
    />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import i18n from '../../js/i18n.js'
import FeedReactionBar from './FeedReactionBar.vue'

const { t } = i18n.global
import FeedRepostComposer from './FeedRepostComposer.vue'
import FeedCardMenu from './FeedCardMenu.vue'
import ShareToChatSheet from '../tamtam/ShareToChatSheet.vue'
import PollDisplay from '../PollDisplay.vue'

const props = defineProps({
  item: { type: Object, required: true },
  currentUserId: { type: String, default: '' },
  chatGroups: { type: Array, default: () => [] },
  userName: { type: String, default: '' },
  userAvatar: { type: String, default: '' },
  canManage: { type: Boolean, default: false },
})

const emit = defineEmits(['react', 'vote', 'comment', 'repost', 'shareToChat', 'edit', 'delete', 'pin'])

const votedId = ref(null)
const showShareToChat = ref(false)
const showRepostComposer = ref(false)

const hasVoted = computed(() => votedId.value !== null)

const hasQuestions = computed(() => !!props.item.poll?.questions?.length)

const initials = computed(() => {
  const name = props.item.authorName || ''
  return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase()
})

const avatarStyle = computed(() => {
  const colors = ['#3B4FCA', '#1A6B42', '#C9A84C', '#6B46C1', '#C0440A']
  const hash = (props.item.authorName || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0)
  return { background: colors[hash % colors.length], color: '#fff' }
})

const userReacted = computed(() => {
  if (!props.item.reactions || !props.currentUserId) return false
  return props.item.reactions.some(r => r.users?.includes(props.currentUserId))
})

const userReactionType = computed(() => {
  if (!props.item.reactions || !props.currentUserId) return 'LIKE'
  const r = props.item.reactions.find(r => r.users?.includes(props.currentUserId))
  return r?.reactionType || 'LIKE'
})

const totalReactions = computed(() => {
  if (!props.item.reactions) return 0
  return props.item.reactions.reduce((sum, r) => sum + (r.count || 0), 0)
})

const shareContent = computed(() => ({
  subject: props.item.text || '',
  message: props.item.text || '',
  url: `${window.location.origin}/post/${props.item.id}`,
  files: [],
}))

const POLL_COLORS = ['#EAF3DE', '#EBF0FC', '#FAEEDA', '#F5F0FF', '#FBEAF0']
const POLL_TEXT_COLORS = ['#27500A', '#0C447C', '#633806', '#6B46C1', '#72243E']

function optionPct(opt) {
  const total = props.item.poll?.totalVotes || 1
  return Math.round((opt.votes / total) * 100)
}

function optionColor(opt) {
  const idx = (props.item.poll?.options || []).indexOf(opt)
  return POLL_COLORS[idx % POLL_COLORS.length]
}

function optionTextColor(opt) {
  const idx = (props.item.poll?.options || []).indexOf(opt)
  return POLL_TEXT_COLORS[idx % POLL_TEXT_COLORS.length]
}

function onVote(opt) {
  if (hasVoted.value) return
  votedId.value = opt.id
  emit('vote', { pollId: props.item.poll?.id, optionId: opt.id })
}

function onReact(reactionType) {
  emit('react', { itemId: props.item.id, reactionType })
}

function onRepostSubmit({ postId, text }) {
  emit('repost', { itemId: postId, text })
}

function onShareToChat(group) {
  emit('shareToChat', { group, feedItem: props.item })
}
</script>

<style scoped>
.fplc {
  background: var(--f7-page-bg-color, #fff);
  border-radius: 10px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  padding: 10px 12px;
  margin-bottom: 8px;
  content-visibility: auto;
  contain-intrinsic-size: 200px;
}
.fplc-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
}
.fplc-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}
.fplc-meta { flex: 1; min-width: 0; }
.fplc-author { font-size: 12px; font-weight: 500; }
.fplc-tag {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 8px;
  flex-shrink: 0;
}
.fplc-tag--gold { background: #FDF6E3; color: #92400E; }
.fplc-pinned {
  display: inline-flex;
  align-items: center;
  gap: 3px;
}
.fplc-sub { font-size: 10px; color: var(--f7-on-surface-variant, #8e8e93); }
.fplc-question {
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  line-height: 1.4;
}
.fplc-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 6px;
}
.fplc-option {
  position: relative;
  border-radius: 6px;
  padding: 7px 10px;
  border: 0.5px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
  overflow: hidden;
  cursor: pointer;
  background: var(--f7-list-bg-color, #f9f9f9);
  text-align: left;
  width: 100%;
}
.fplc-option--voted {
  border-color: var(--avocash-green, #1A6B42);
}
.fplc-option--disabled {
  cursor: default;
}
.fplc-option-bg {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  border-radius: 6px;
  transition: width 0.4s ease;
}
.fplc-option-content {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.fplc-option-label {
  font-size: 11px;
  color: var(--f7-text-color, #000);
}
.fplc-option-pct {
  font-size: 11px;
  font-weight: 600;
}
.fplc-footer {
  margin-bottom: 4px;
}
.fplc-display {
  margin-bottom: 6px;
}
.fplc-hint {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
</style>
